import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import usePageTitle from '../hooks/usePageTitle';
import useCanonicalTag from '../hooks/useCanonicalTag';
import useMetaTags from '../hooks/useMetaTags';
import '../styles/fonts.css';
import '../styles/all_news.css';
import Header from '../components/Header';
import Footer from '../components/Footer';
import UpcomingEvents from '../components/UpcomingEvents';
import { getArticles } from '../services/api';

const SOURCE_LABELS = {
  iracing_news: 'iRacing',
  iracing_blog: 'iRacing',
  srss: 'Sim Racing Sem Segredos',
  internal: 'SimRace Central',
};

const SOURCE_COLORS = {
  iracing_news: '#0073e6',
  iracing_blog: '#0073e6',
  srss: '#e65c00',
  internal: '#ff4747',
};

function NewsCard({ article }) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language === 'pt-BR' ? 'pt-BR' : 'en-US';
  const label = SOURCE_LABELS[article.source] || article.source_label;
  const color = SOURCE_COLORS[article.source] || '#ff4747';

  function formatDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString(locale, { day: '2-digit', month: 'short', year: 'numeric' });
  }

  const cardContent = (
    <article className="news-card">
      <div
        className="news-thumb"
        style={{
          backgroundImage: article.thumbnail_url ? `url('${article.thumbnail_url}')` : 'none',
          backgroundColor: article.thumbnail_url ? undefined : '#1b2127',
        }}
      />
      <div className="news-info">
        <span className="news-source-badge" style={{ backgroundColor: color }}>{label}</span>
        <h4>{article.title}</h4>
        {article.summary && <p>{article.summary}</p>}
        <div className="news-meta">
          <span>{formatDate(article.published_at)}</span>
          {article.is_external && <span className="external-indicator">↗ {t('news.external')}</span>}
        </div>
      </div>
    </article>
  );

  if (article.is_external) {
    return (
      <a href={article.external_url} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
        {cardContent}
      </a>
    );
  }
  return (
    <Link to={`/news/${article.slug}`} style={{ textDecoration: 'none', color: 'inherit' }}>
      {cardContent}
    </Link>
  );
}

function News() {
  const { t } = useTranslation();
  usePageTitle('News');
  useCanonicalTag();
  useMetaTags({
    title: 'News | Celeritas',
    description: 'Latest sim racing and iRacing news, updates and community articles.',
    url: 'https://celeritas.live/news',
  });
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [activeFilter, setActiveFilter] = useState(null);

  const FILTERS = [
    { label: t('common.all'), value: null },
    { label: 'iRacing', value: 'iracing_news' },
    { label: 'iRacing Blog', value: 'iracing_blog' },
    { label: 'Sim Racing Sem Segredos', value: 'srss' },
  ];

  useEffect(() => {
    setLoading(true);
    getArticles(page, activeFilter)
      .then(data => {
        setArticles(data.results);
        setTotalPages(data.total_pages);
        setError(null);
      })
      .catch(() => setError(t('news.loadError')))
      .finally(() => setLoading(false));
  }, [page, activeFilter]);

  const handleFilterChange = (value) => {
    setActiveFilter(value);
    setPage(1);
  };

  return (
    <div className="NewsPage">
      <Header />

      <main>
        <section id="hero-banner">
          <div className="hero-content">
            <div className="hero-text">
              <h1 className="hero-title">{t('home.hero_title')}</h1>
              <p className="hero-sub">{t('home.hero_subtitle')}</p>
            </div>
            <div className="hero-actions">
              <Link to="/add-league" className="hero-btn-primary">{t('common.addMyLeague')}</Link>
              <Link to="/leagues" className="hero-btn-secondary">{t('common.viewLeagues')}</Link>
            </div>
          </div>
        </section>

        <div className="news-layout">
          <section className="news-archive" style={{ flex: 1 }}>
            <div className="box-header">
              <h3>{t('news.archive')}</h3>
            </div>

            <div className="news-filters" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', marginBottom: '16px' }}>
              {FILTERS.map(f => (
                <button
                  key={String(f.value)}
                  className={`filter-btn${activeFilter === f.value ? ' active' : ''}`}
                  onClick={() => handleFilterChange(f.value)}
                >
                  {f.label}
                </button>
              ))}
            </div>

            {loading && <p style={{ color: '#9ba3ab' }}>{t('news.loading')}</p>}
            {error && <p style={{ color: 'red' }}>{error}</p>}

            {!loading && !error && (
              <>
                <div className="news-list-full">
                  {articles.length === 0 && (
                    <p style={{ color: '#9ba3ab' }}>{t('news.notFound')}</p>
                  )}
                  {articles.map(article => (
                    <NewsCard key={article.id} article={article} />
                  ))}
                </div>

                {totalPages > 1 && (
                  <nav className="pagination-container">
                    <button
                      className={`page-nav${page === 1 ? ' disabled' : ''}`}
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      « {t('common.previous')}
                    </button>
                    <div className="page-numbers">
                      {Array.from({ length: totalPages }, (_, i) => i + 1).map(p => (
                        <button
                          key={p}
                          className={`page-num${p === page ? ' active' : ''}`}
                          onClick={() => setPage(p)}
                        >
                          {p}
                        </button>
                      ))}
                    </div>
                    <button
                      className={`page-nav${page === totalPages ? ' disabled' : ''}`}
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      {t('common.next')}
                    </button>
                  </nav>
                )}
              </>
            )}
          </section>

          <aside className="right-col">
            <section className="course-banner">
              <h3>Sim Racing Sem Segredos</h3>
              <p>{t('news.srssDesc')}</p>
              <a href="https://simracingsemsegredos.com.br" target="_blank" rel="noopener noreferrer" className="btn-course">{t('news.srssBtn')}</a>
            </section>
            <UpcomingEvents />
          </aside>
        </div>
      </main>

      <Footer />
    </div>
  );
}

export default News;
